import { autoTranslate } from "@web-monorepo/translation-runtime";
import type { TranslatedString } from "@web-monorepo/i18next";

export type ProgrammerReadableErrorType =
  | "default"
  | "not-found"
  | "not-allowed"
  | "forbidden"
  | "temporary-redirect"
  | "request-blocked"
  | "server-error"
  | "rate-limited"
  | "bad-request"
  | "service-unavailable"
  | "service-unavailable-load-shedding"
  | "gateway-timeout"
  | "bad-gateway"
  | "module-load-error"
  | "no-network";

export type HttpStatusCode = 400 | 401 | 403 | 404 | 429 | 500 | 502 | 503 | 504 | 307;

export interface ErrorConfig {
  svgUrl: string | null;
  title: TranslatedString;
  message: TranslatedString;
  showReloadButton: boolean;
}

// These are functions so that every invocation gets a correctly-translated string:
const ERROR_CONFIG_FACTORIES: Record<ProgrammerReadableErrorType, () => ErrorConfig> = {
  default: () => ({
    svgUrl: null,
    title: autoTranslate("Something went wrong"),
    message: autoTranslate("We encountered an unexpected error."),
    showReloadButton: true,
  }),

  "not-found": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_404.svg",
    title: autoTranslate("Page not found"),
    message: autoTranslate("Whoops! The page you're looking for doesn't exist."),
    showReloadButton: false,
  }),

  "not-allowed": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_401.svg",
    title: autoTranslate("Session timed out"),
    message: autoTranslate("Whoops! It looks like your session has timed out."),
    showReloadButton: true,
  }),

  forbidden: () => ({
    svgUrl: "https://static.classdojo.com/errors/error_401.svg",
    title: autoTranslate("Access Denied"),
    message: autoTranslate("Whoops! Looks like you don't have permission to access this resource."),
    showReloadButton: true,
  }),

  // Generic 307 Temporary Redirect error.
  // Note: 307 errors can have different meanings:
  // - Standard temporary redirect (handled here)
  // - Request blocked by filtering service/MITM (see request-blocked type and is307RequestBlocked helper)
  "temporary-redirect": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_307.svg",
    title: autoTranslate("Something went wrong"),
    message: autoTranslate("We encountered an unexpected error."),
    showReloadButton: true,
  }),

  // 307 error from a filtering service or MITM blocking the request.
  // Common in school networks where content filters intercept requests.
  // Use is307RequestBlocked() to detect this scenario.
  "request-blocked": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_307.svg",
    title: autoTranslate("Request blocked"),
    message: autoTranslate(
      "Whoops! Your network's filtering service is preventing this request. This could be a school firewall or content filter. Please contact your network administrator or try again from a different network.",
    ),
    showReloadButton: true,
  }),

  "rate-limited": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_429.svg",
    title: autoTranslate("Slow down!"),
    message: autoTranslate(
      "Whoops! Looks like you're making too many requests too quickly. Please wait 10 seconds before continuing to use the site.",
    ),
    showReloadButton: true,
  }),

  "bad-request": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_400.svg",
    title: autoTranslate("Something went wrong"),
    message: autoTranslate("We encountered an unexpected error."),
    showReloadButton: true,
  }),

  "server-error": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_500.svg",
    title: autoTranslate("Something went wrong"),
    message: autoTranslate("Whoops! Something went wrong on our end. Please try again in a few moments."),
    showReloadButton: true,
  }),

  "bad-gateway": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_502.svg",
    title: autoTranslate("Bad Gateway"),
    message: autoTranslate(
      "Whoops! We're having trouble connecting to our servers. Please try again in a few moments.",
    ),
    showReloadButton: true,
  }),

  "service-unavailable": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_503.svg",
    title: autoTranslate("Service temporarily unavailable"),
    message: autoTranslate(
      "Whoops! Our servers are temporarily unavailable. We're working on it! Please try again in a few moments.",
    ),
    showReloadButton: true,
  }),

  // 503 error specifically caused by load shedding.
  // Load shedding is when servers temporarily reduce load to prevent system-wide failures.
  "service-unavailable-load-shedding": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_503.svg",
    title: autoTranslate("Experiencing high traffic"),
    message: autoTranslate(
      "Whoops! ClassDojo is experiencing unusually high traffic right now. We're working to restore normal service. Please try again in a few moments.",
    ),
    showReloadButton: true,
  }),

  "gateway-timeout": () => ({
    svgUrl: "https://static.classdojo.com/errors/error_504.svg",
    title: autoTranslate("Request timed out"),
    message: autoTranslate("Whoops! The request took too long to complete. Please try again in a few moments."),
    showReloadButton: true,
  }),

  // Module load errors occur when dynamic imports fail, typically due to:
  // - Unstable network connection
  // - Cached files being outdated after a deployment
  // - CDN issues
  "module-load-error": () => ({
    svgUrl: null,
    title: autoTranslate("Failed to load page"),
    message: autoTranslate(
      "Whoops! There was an error loading the page. This is likely caused by an unstable internet connection or outdated files. Please reload the page to try again.",
    ),
    showReloadButton: true,
  }),

  // No network errors occur when there's no internet connection or network failure
  // before any response is received (e.g., offline, DNS failure, connection refused)
  "no-network": () => ({
    svgUrl: null,
    title: autoTranslate("No internet connection"),
    message: autoTranslate(
      "Whoops! It looks like you're not connected to the internet. Please check your connection and try again.",
    ),
    showReloadButton: true,
  }),
};

const HTTP_STATUS_TO_ERROR_TYPE: Record<HttpStatusCode, ProgrammerReadableErrorType> = {
  307: "temporary-redirect",
  400: "bad-request",
  401: "not-allowed",
  403: "forbidden",
  404: "not-found",
  429: "rate-limited",
  500: "server-error",
  502: "bad-gateway",
  503: "service-unavailable",
  504: "gateway-timeout",
};

export function getErrorConfig(errorType: ProgrammerReadableErrorType): ErrorConfig {
  const factory = ERROR_CONFIG_FACTORIES[errorType];
  return factory();
}

export function errorTypeFromHttpStatus(status: HttpStatusCode): ProgrammerReadableErrorType {
  return HTTP_STATUS_TO_ERROR_TYPE[status] ?? "default";
}

/**
 * Determines if a 307 error is from a filtering service/MITM blocking the request.
 * This is a common scenario in school networks where filtering services intercept
 * and block certain requests.
 *
 * Checks both the response body and error message for indicators of filtering service blocks.
 */
export function is307RequestBlocked(
  error: Error & { response?: { status: number; body?: unknown; text?: string } },
): boolean {
  if (error.response?.status === 307) {
    // Content filters answer with an HTML block page. `apiFetch` parses `text/html` into `body`
    // verbatim, but a JSON content-type that fails to parse leaves `body` undefined and keeps the
    // payload only on `text`. That's why, below, we check both body and text.
    const { body, text } = error.response;
    try {
      // Handle different body types (string, object with toString, etc.)
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Best effort to handle different body types.
      const bodyStr = typeof body === "string" ? body : body ? String(body) : "";
      if (bodyStr.includes("Request Blocked")) {
        return true;
      }
      // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
    } catch {
      // If stringification fails, continue to other checks
    }

    if ((text ?? "").includes("Request Blocked")) {
      return true;
    }
  }

  // Also check error message for filtering service indicators
  if (error.message.includes("Empty response from filtering service")) {
    return true;
  }

  // Legacy check: some implementations check the error message
  if (error.message.includes("Error returned in API call: 307") && error.message.includes("Request Blocked")) {
    return true;
  }

  return false;
}

/**
 * Determines if an unknown thrown value is a module load error.
 * These happen when dynamic imports fail, often due to network issues or outdated cached files.
 */
export function isModuleLoadError(error: unknown): boolean {
  let currentError: unknown = error;
  const visitedErrors = new Set<Error>();

  while (currentError instanceof Error && !visitedErrors.has(currentError)) {
    visitedErrors.add(currentError);

    if (
      currentError.name === "ChunkLoadError" ||
      currentError.message.includes("Failed to fetch dynamically imported module") ||
      currentError.message.includes("Importing a module script failed") ||
      currentError.message.includes("error loading dynamically imported module") ||
      // Vite's preload helper fetches a lazy chunk's stylesheets alongside its JS, and rejects
      // with this message when a <link> fails. Despite naming CSS rather than a module, it is
      // the same dynamic-import failure and should be classified as one.
      currentError.message.includes("Unable to preload CSS for") ||
      /Loading chunk \S+ failed/.test(currentError.message)
    ) {
      return true;
    }

    currentError = currentError.cause;
  }

  return false;
}

/**
 * Determines if a 503 error is specifically caused by load shedding.
 * Load shedding is when servers temporarily reduce load to prevent system-wide failures.
 */
export function isLoadSheddingError(
  error: Error & { response?: { status: number; body?: { error?: { message?: string } } } },
): boolean {
  if (error.response?.status === 503) {
    const errorMessage = error.response.body?.error?.message ?? "";
    return errorMessage.toLowerCase().includes("load shedding");
  }
  return false;
}

// Re-export error classifiers from @web-monorepo/fetchers so all consumers can
// import from a single canonical location.
export {
  isNoNetworkError,
  isEmptyResponseBodyError,
  isRawNetworkError,
  isConnectivityError,
} from "@web-monorepo/fetchers";

/**
 * Determines if an error is an InvalidCollectionResponseError.
 * This occurs when a collection response is received but missing the required _items field,
 * which indicates an API contract violation.
 */
export function isInvalidCollectionResponseError(error: Error): boolean {
  return error.name === "InvalidCollectionResponseError";
}

/**
 * Determines if a 400 bad request error is ignorable (shouldn't be reported as a critical error).
 * These typically indicate user-generated bad input like invalid IDs in URLs.
 */
export function isIgnorableBadRequest(
  error: Error & { response?: { status: number; body?: { error?: { detail?: string } } } },
): boolean {
  if (error.response?.status !== 400) {
    return false;
  }

  const ignorableDetails = [
    "Invalid id",
    "Invalid targetId",
    "Invalid classId",
    "ChannelId was invalid",
    "Invalid marketplaceFunnelUUId",
  ];

  const detail = error.response.body?.error?.detail;
  return detail ? ignorableDetails.includes(detail) : false;
}
